"use client";

import React, { useEffect, useMemo, useRef } from "react";

import { useSelector } from "@stadium/services/plasmic";
import { CE_05, DATA_PROVIDER_STORE_MENU } from "@helpers/plasmic/slices";

import { getCategoriesMap } from "@stadium/shared/src/helpers/plasmic/static-slices";

import CatalogEntries, {
  CatalogEntriesProps,
} from "@components/swagmagic/dynamic/slices/catalog-entries";
import { getCategoriesToRender } from "@helpers/plasmic/catalog-entries";

const emptyObject = {};

const CatalogEntriesClient = (props: Omit<CatalogEntriesProps, "dynamicSlicesData">) => {
  const { dynamicSlicesData } = useSelector(DATA_PROVIDER_STORE_MENU);

  const {
    setControlContextData,
    variant,
    viewType,
    showSubcategories,
    categorySlug,
    customCategories,
    hideCategories,
    visibleCategoryCount,
    orderCategories,
  } = props;

  const {
    menu,
    parentCategories: parentCategoriesData,
    allCategories: allCategoriesData,
  } = dynamicSlicesData || emptyObject;

  const { categoriesToRender, sliceCategories, isCustomCategories } = useMemo(
    () =>
      getCategoriesToRender({
        ...props,
        allCategoriesData,
        parentCategoriesData,
        skipTitleHumanization: menu?.skipTitleHumanization || false,
      }),
    [
      viewType,
      variant,
      showSubcategories,
      categorySlug,
      customCategories,
      hideCategories,
      visibleCategoryCount,
      orderCategories,
      allCategoriesData,
      parentCategoriesData,
      menu?.skipTitleHumanization,
    ],
  );

  // Create controlContextData - we'll compare serialized version to prevent unnecessary updates
  const controlContextData = {
    slugs: getCategoriesMap(sliceCategories),
    renderedSlugs: getCategoriesMap(isCustomCategories ? categoriesToRender : sliceCategories),
    categorySlugs: getCategoriesMap(showSubcategories ? allCategoriesData : parentCategoriesData),
  };

  // Use ref to track previous serialized controlContextData to prevent unnecessary updates
  const prevControlContextDataKeyRef = useRef<string>();

  useEffect(() => {
    if (variant !== CE_05 && setControlContextData) {
      const controlContextDataKey = JSON.stringify(controlContextData);

      // Only update if the actual data changed (not just reference)
      if (prevControlContextDataKeyRef.current !== controlContextDataKey) {
        prevControlContextDataKeyRef.current = controlContextDataKey;
        setControlContextData(controlContextData);
      }
    }
  }, [variant, setControlContextData, controlContextData]);

  return (
    <CatalogEntries
      {...props}
      dynamicSlicesData={dynamicSlicesData}
      controlContextData={controlContextData}
    />
  );
};

export default CatalogEntriesClient;
